import { useTheme } from '@emotion/react';
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ConfigurationStatus } from '@prisma/client';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { Spacer } from 'src/components';
import NPIButton from 'src/components/Button';
import { NPITextareaField } from 'src/components/FormikComponents';
import { useUpdateSuspendedReason } from 'src/hooks';

const EditSuspendedReasonModalContentValidationSchema = yup.object().shape({
    description: yup.string().required('Suspend reason required'),
});
interface EditSuspendedReasonModalContentProps {
    selectedReasonId: number;
    description: string;
    status: string;
    onCancel: () => void;
    onConfirm: () => void;
}

const EditSuspendedReasonModalContent = ({
    selectedReasonId,
    description,
    status,
    onCancel,
    onConfirm,
}: EditSuspendedReasonModalContentProps) => {
    const theme = useTheme();
    const { onUpdateSuspendedReason } = useUpdateSuspendedReason(onConfirm);
    const handleFormSubmit = async (values) => {
        await onUpdateSuspendedReason(values);
    };

    return (
        <Formik
            validationSchema={EditSuspendedReasonModalContentValidationSchema}
            initialValues={{
                id: selectedReasonId,
                status,
                description,
            }}
            onSubmit={handleFormSubmit}
        >
            {({ errors, setFieldValue }) => {
                return (
                    <Form>
                        <Stack width="100%" flexDirection="column" p={2} spacing={2}>
                            <Stack
                                width="100%"
                                flexDirection="row"
                                justifyContent="start"
                                alignItems="center"
                            >
                                <Stack pr={2}>
                                    <Typography>Status</Typography>
                                </Stack>

                                <FormControl>
                                    <RadioGroup
                                        aria-labelledby="status-radio-buttons-group-label"
                                        defaultValue={status}
                                        name="status"
                                        row
                                        onChange={(event) => {
                                            setFieldValue('status', event.target.value);
                                        }}
                                    >
                                        <FormControlLabel
                                            value={ConfigurationStatus.ACTIVE}
                                            control={<Radio />}
                                            label="Active"
                                        />
                                        <FormControlLabel
                                            value={ConfigurationStatus.INACTIVE}
                                            control={<Radio />}
                                            label="Inactive"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <Stack width="100%" flexDirection="column" justifyContent="start">
                                <Stack pb={1}>
                                    <Typography fontWeight="bold">Suspend Reason</Typography>
                                </Stack>
                                <Stack justifyContent="center">
                                    <NPITextareaField
                                        fieldName="description"
                                        defaultValue={description}
                                        error={errors?.description as string}
                                    />
                                </Stack>
                            </Stack>

                            <Stack
                                width="100%"
                                flexDirection="row"
                                justifyContent="end"
                                alignItems="flex-end"
                            >
                                <NPIButton
                                    variant="contained"
                                    sx={{
                                        bgcolor: theme.palette.grey.A700,
                                        color: theme.palette.common.white,
                                        width: '25%',
                                    }}
                                    onClick={onCancel}
                                    label="Cancel"
                                />
                                <Spacer padding={1} />
                                <NPIButton
                                    type="submit"
                                    variant="contained"
                                    disabled={Object.values(errors).length > 0}
                                    sx={{
                                        bgcolor: theme.palette.common.black,
                                        color: theme.palette.common.white,
                                        width: '25%',
                                    }}
                                    label="Confirm"
                                />
                            </Stack>
                        </Stack>
                    </Form>
                );
            }}
        </Formik>
    );
};
export default EditSuspendedReasonModalContent;
